import * as R from 'ramda';
import { Endpoints } from '../../../../core/api/endpoints';
import { ApiReducer, makeApiReducer, makeApiAction, makeRsaaTypes } from '../../../../core/api/rsaa';
import { addError, noLoadingNoError, addLoading, getLoading } from '../../../../core/api/module';
import { ActionType } from '../../../../core/redux/actionType';
import { UpdateFieldAnswerRequest } from '../../models';
import { updateFieldAnswer } from '../module';


const KEY_ACTION = ActionType.UpdateDevPlanFieldAnswerRequest;
const ACTIONS    = [
  ActionType.UpdateDevPlanFieldAnswerRequest,
  ActionType.UpdateDevPlanFieldAnswerResponse,
  ActionType.UpdateDevPlanFieldAnswerError,
];


type Payload = { devFieldAnswerId: number } | undefined;
type Meta = UpdateFieldAnswerRequest;


const keyForMeta = (meta: Meta) => `${meta.devActionId}-${meta.devFieldId}`;


/**
 * Dispatch this action to update the list of dev plan action fields and answers
 */
export const updateDevPlanFieldAnswerAction = (
  fieldAnswer: UpdateFieldAnswerRequest,
) => makeApiAction<Payload, Meta>({
  method:   'POST',
  endpoint: Endpoints.UpdateDevPlanFieldAnswer,
  headers:  { 'content-type': 'application/json' },
  body:     JSON.stringify({ ...fieldAnswer, devFieldId: isNaN(+fieldAnswer.devFieldId) ? 0 : fieldAnswer.devFieldId}),
  types:    makeRsaaTypes<Payload, Meta>(ACTIONS, fieldAnswer),
  bailout:  state => !!getLoading(state, KEY_ACTION, keyForMeta(fieldAnswer)),
});


const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const key = keyForMeta(action.meta);

  if (isLoading) {
    return addLoading(state, KEY_ACTION, key);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, key);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, key);

  // Assert not null because: how is the user updating an item we haven't loaded?
  const toUpdate = successState.devPlan?.fieldsAnswers?.[`${action.meta.devItemId}`]?.[`${action.meta.devActionId}`]!;

  // Update the relevant field/answer to reflect the user's changes
  const updated = updateFieldAnswer(toUpdate, action.meta);

  // The server returns the answer's row ID after insert/update. Patch it onto the matching field so subsequent saves
  // can target the row by primary key (otherwise the server would have to keep falling back to the natural key).
  const answerId = payload?.devFieldAnswerId;
  const finalList = answerId
    ? updated.map(f => (
        f.devPlanFieldId === action.meta.devFieldId
        && f.devActionId === action.meta.devActionId
        && f.devPlanFieldAnswerId !== answerId
      )
        ? { ...f, devPlanFieldAnswerId: answerId }
        : f,
      )
    : updated;

  return R.assocPath(
    ['devPlan', 'fieldsAnswers', `${action.meta.devItemId}`, `${action.meta.devActionId}`],
    finalList,
    successState,
  );
};


export const updateDevPlanFieldAnswerReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
